import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useScenarioSummary } from "@/hooks/world";
import { useCanEdit } from "@/model/access";
import { fmtValue, pct } from "@/lib/format";
import { fmtMonth, idx, monthAt } from "@/lib/months";
import { cn } from "@/lib/utils";
import { drivers } from "@/model/graph";
import { useStore, useTerm } from "@/model/store";
import type { ModelNode, Lever, Scenario } from "@/model/types";
import { biggestChange, downstream, isCalculated } from "./scenarioText";

type EventLever = Extract<Lever, { kind: "event" }>;

/** Months a scenario can pin an event to. */
const MONTHS = Array.from({ length: idx("2030-12") - idx("2026-10") + 1 }, (_, i) => monthAt(idx("2026-10") + i));

/** Inline select styled as an editable word in a sentence. */
const inlineTrigger =
	"h-[32px]! w-auto gap-1 rounded-md border-0 border-b border-dashed border-foreground/40 bg-transparent px-1.5 font-medium shadow-none hover:bg-[var(--scenario)]/10 dark:bg-transparent pointer-coarse:h-[44px]!";

const HAPPENS_SHORT: Record<EventLever["happens"], string> = { true: "at some point", at: "in", by: "by", false: "doesn’t happen" };

/** "in ▾ Mar 2027 ▾ (Assume ▾)": an event lever's outcome, month and mode as inline controls (review E1). */
export function EventLeverControls({ lever, onChange }: { lever: EventLever; onChange: (l: EventLever) => void }) {
	const term = useTerm();
	const month = lever.month ?? "2027-06";
	return (
		<>
			<Select value={lever.happens} onValueChange={(v) => onChange({ ...lever, happens: v as EventLever["happens"], month })}>
				<SelectTrigger className={inlineTrigger} aria-label="When it happens">
					<SelectValue>{HAPPENS_SHORT[lever.happens]}</SelectValue>
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="true">happens at some point</SelectItem>
					<SelectItem value="at">happens in a given month</SelectItem>
					<SelectItem value="by">happens by a given month</SelectItem>
					<SelectItem value="false">doesn’t happen</SelectItem>
				</SelectContent>
			</Select>
			{(lever.happens === "at" || lever.happens === "by") && (
				<Select value={month} onValueChange={(m) => onChange({ ...lever, month: m })}>
					<SelectTrigger className={inlineTrigger} aria-label="Month">
						<SelectValue>{fmtMonth(month)}</SelectValue>
					</SelectTrigger>
					<SelectContent className="max-h-72">
						{MONTHS.map((m) => (
							<SelectItem key={m} value={m}>
								{fmtMonth(m)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			)}
			<span className="inline-flex items-center text-muted-foreground">
				(
				<Select value={lever.mode} onValueChange={(v) => onChange({ ...lever, mode: v as EventLever["mode"] })}>
					<SelectTrigger className={cn(inlineTrigger, "text-foreground")} aria-label="Assume or make it so">
						<SelectValue>{lever.mode === "assume" ? term("Assume", "conditioning") : term("Make it so", "do()")}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="assume">
							<span>
								{term("Assume", "conditioning")}
								<span className="block text-xs text-muted-foreground">Suppose this turns out true: what probably caused it updates too</span>
							</span>
						</SelectItem>
						<SelectItem value="make">
							<span>
								{term("Make it so", "do()")}
								<span className="block text-xs text-muted-foreground">Force this: only what’s downstream changes</span>
							</span>
						</SelectItem>
					</SelectContent>
				</Select>
				)
			</span>
		</>
	);
}

/** "Happens in" means that exact month; most planners mean "by" (review E2). */
export function ExactMonthHint({ lever, onChange, className }: { lever: EventLever; onChange: (l: EventLever) => void; className?: string }) {
	if (lever.happens !== "at") return null;
	return (
		<p className={cn("text-xs text-muted-foreground", className)}>
			Only that exact month counts, which makes the scenario rare. Most plans mean “by”.{" "}
			<button type="button" className="font-medium text-foreground underline underline-offset-2" onClick={() => onChange({ ...lever, happens: "by" })}>
				Use “by {fmtMonth(lever.month ?? "2027-06")}”
			</button>
		</p>
	);
}

/**
 * On an event or metric page while a scenario is selected: what this node does in the
 * scenario, and how to change it. Values are edited on the chart and table, like the baseline.
 */
export function NodeScenarioStrip({ node, scenario }: { node: ModelNode; scenario: Scenario }) {
	const nodes = useStore((s) => s.world.nodes);
	const links = useStore((s) => s.world.links);
	const upsert = useStore((s) => s.upsertLever);
	const remove = useStore((s) => s.removeLever);
	const setActive = useStore((s) => s.setActiveScenario);
	const lever = scenario.levers.find((l) => l.node === node.id);
	const canEdit = useCanEdit();
	const calculated = isCalculated(node);
	const summary = useScenarioSummary(scenario.id);
	const changes = summary.data?.summary.changes ?? null;
	const below = useMemo(() => (lever ? downstream(node.id, nodes, links) : undefined), [lever, node.id, nodes, links]);
	// After an edit here, the biggest change it makes further down (review 7.3), from the API's "What changes".
	const change = lever && changes ? biggestChange(nodes, changes, { within: below }) : null;

	const pill = <span className="rounded-full bg-[var(--scenario)] px-2.5 py-0.5 text-xs font-medium text-white">{scenario.name}</span>;
	const reset = (id: string) => (
		<>
			<span className="text-muted-foreground" aria-hidden>
				·
			</span>
			<Button size="sm" variant="link" className="h-[32px] px-1 text-foreground pointer-coarse:h-[44px]" onClick={() => void remove(scenario.id, id)}>
				Reset
			</Button>
		</>
	);

	let sentence: React.ReactNode;
	let extra: React.ReactNode = null;
	if (calculated) {
		const inputs = drivers(node.id, nodes, links).map((id) => nodes[id]);
		const own = changes?.find((c) => c.handle === node.id && c.moved);
		const f = (v: number) => (own?.kind === "event" ? pct(v) : fmtValue(v, own?.unit ?? ""));
		sentence = (
			<>
				{pill}
				<span>Calculated, so it follows</span>
				{inputs.map((n, i) => (
					<span key={n.id} className="inline-flex items-center gap-1.5">
						{i > 0 && (
							<span className="text-muted-foreground" aria-hidden>
								·
							</span>
						)}
						<Link
							to={`/node/${n.id}`}
							className="inline-flex min-h-[32px] items-center rounded-full border bg-card px-2.5 text-xs font-medium hover:border-[var(--scenario)] pointer-coarse:min-h-[44px]"
						>
							{n.title}
						</Link>
					</span>
				))}
				{own && (
					<span className="text-muted-foreground">
						· here {f(own.baseline)} → <span className="font-medium text-foreground">{f(own.scenario)}</span>
					</span>
				)}
			</>
		);
	} else if (!lever) {
		sentence = (
			<>
				{pill}
				{node.kind === "metric" ? (
					<>
						<span>Uses the baseline values.</span>
						{canEdit && (
							<Button
								size="sm"
								variant="outline"
								className="h-[32px] bg-card pointer-coarse:h-[44px]"
								onClick={() => void upsert(scenario.id, { id: node.id, kind: "range", node: node.id, range: node.range })}
							>
								Give it its own values
							</Button>
						)}
					</>
				) : (
					<span>Uses the baseline. Drag the chart to give it its own values.</span>
				)}
				{node.kind === "event" && (
					<Select
						value=""
						onValueChange={(v) =>
							void upsert(scenario.id, {
								id: node.id,
								kind: "event",
								node: node.id,
								happens: v as "true" | "at" | "by" | "false",
								month: "2027-06",
								mode: "assume",
							})
						}
					>
						<SelectTrigger size="sm" className="h-[32px]! w-auto bg-card pointer-coarse:h-[44px]!">
							<SelectValue placeholder="Or assume it…" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="true">Assume it happens</SelectItem>
							<SelectItem value="by">Assume it happens by…</SelectItem>
							<SelectItem value="at">Assume it happens in an exact month…</SelectItem>
							<SelectItem value="false">Assume it doesn’t happen</SelectItem>
						</SelectContent>
					</Select>
				)}
			</>
		);
	} else if (lever.kind === "event") {
		sentence = (
			<>
				{pill}
				<span>{lever.mode === "make" ? "makes it so" : "assumes"}</span>
				<span className="font-medium">{node.title}</span>
				<EventLeverControls lever={lever} onChange={(l) => void upsert(scenario.id, l)} />
				{reset(lever.node)}
			</>
		);
		extra = <ExactMonthHint lever={lever} onChange={(l) => void upsert(scenario.id, l)} className="basis-full" />;
	} else {
		sentence = (
			<>
				{pill}
				<span>Has its own values here. Drag the chart or edit the table to change them.</span>
				{reset(lever.node)}
			</>
		);
	}

	return (
		<div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-[var(--scenario)]/50 bg-[var(--scenario)]/5 px-4 py-2 text-sm">
			<div className="flex min-w-0 flex-[1_1_36rem] flex-wrap items-center gap-x-1.5 gap-y-1">{sentence}</div>
			<div className="ml-auto flex shrink-0 items-center gap-1">
				{!change && (
					<Button size="sm" variant="ghost" className="pointer-coarse:h-[44px]" asChild>
						<Link to={`/scenario/${scenario.id}`}>Open scenario</Link>
					</Button>
				)}
				<Button size="sm" variant="ghost" className="pointer-coarse:h-[44px]" onClick={() => setActive(null)}>
					Back to Baseline
				</Button>
			</div>
			{change && (
				<p className="basis-full pb-1 text-muted-foreground">
					<span className="text-foreground">
						{change.node.title}: {change.from} → <span className="font-semibold">{change.to}</span>.
					</span>{" "}
					<Link to={`/scenario/${scenario.id}`} className="font-medium text-foreground underline underline-offset-2 hover:text-[var(--scenario)]">
						Open scenario
					</Link>{" "}
					for all changes.
				</p>
			)}
			{extra}
		</div>
	);
}
